import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useIntegrations, type Integration } from "@/api/integrations";
import { DataTable, type ColumnDef } from "@/components/data-table/DataTable";
import { useDataTableState } from "@/components/data-table/useDataTableState";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";

import { EditIntegrationDialog } from "./EditIntegrationDialog";

export default function Integrations() {
  const { t } = useTranslation();

  const ds = useDataTableState({
    defaultSort: { id: "name", desc: false },
    defaultPageSize: 50,
    filterDeps: [],
  });

  const query = useIntegrations();

  const [editing, setEditing] = useState<Integration | null>(null);

  const columns = useMemo<ColumnDef<Integration>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("settings.integrations.columnName"),
      },
      {
        accessorKey: "type",
        header: t("settings.integrations.columnType"),
        size: 160,
      },
      {
        id: "status",
        header: t("settings.integrations.columnStatus"),
        size: 120,
        cell: ({ row }) => (
          <Badge variant={row.original.enabled ? "default" : "secondary"}>
            {row.original.enabled
              ? t("settings.integrations.enabled")
              : t("settings.integrations.disabled")}
          </Badge>
        ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <PageHeader title={t("settings.integrations.title")} />

      <DataTable<Integration>
        sorting={ds.sorting}
        onSortingChange={ds.setSorting}
        pagination={ds.pagination}
        onPaginationChange={ds.setPagination}
        data={query.data ?? []}
        totalCount={query.data?.length ?? 0}
        isLoading={query.isLoading}
        columns={columns}
        emptyLabel={t("settings.integrations.empty")}
        getRowId={(r) => String(r.id)}
        onRowClick={(row) => setEditing(row)}
      />

      <EditIntegrationDialog
        integration={editing}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}
